// src/services/appVersionApi.js
//
// Force update / sürüm kontrolü uçları ve sürüm karşılaştırma yardımcıları.
//
// Semantik (UI metinleri buna göre yazılmıştır):
//   minSupportedVersion — zorunlu güncelleme eşiği. BU SÜRÜMÜN ALTINDAKİ kullanıcı
//                         uygulamayı kullanamaz. Eşiğe EŞİT sürüm zorlanmaz.
//   latestVersion       — güncel sürüm. Altındaki (ama min ve üstündeki) kullanıcıya
//                         güncelleme yalnızca ÖNERİLİR, kapatılabilir.
//   enabled: false      — o platformda hiçbir kullanıcıya hiçbir şey gösterilmez.

import apiClient from './ApiClient';

export const PLATFORMS = [
    {
        value: 'ios',
        label: 'iOS',
        storeLabel: 'App Store URL\'i',
        storePlaceholder: 'https://apps.apple.com/app/id0000000000',
    },
    {
        value: 'android',
        label: 'Android',
        storeLabel: 'Play Store URL\'i',
        storePlaceholder: 'https://play.google.com/store/apps/details?id=com.utkan_esim_world',
    },
];

export const platformLabel = (value) =>
    PLATFORMS.find((p) => p.value === value)?.label || value || '—';

// --- Sürüm karşılaştırma -------------------------------------------------

// Backend'in kabul ettiği format. Segment sayısı serbest: "2", "1.4", "1.4.12".
export const VERSION_PATTERN = /^\d+(\.\d+)*$/;

export const isValidVersion = (value) =>
    typeof value === 'string' && VERSION_PATTERN.test(value.trim());

/**
 * Sürümleri SAYISAL segment bazında karşılaştırır. String karşılaştırma yanlış
 * sonuç verir: "1.10" < "1.9" olurdu. Eksik segment 0 sayılır ("1.2" === "1.2.0").
 * @returns {number} a < b ise negatif, a === b ise 0, a > b ise pozitif.
 */
export const compareVersions = (a, b) => {
    const as = String(a || '').trim().split('.');
    const bs = String(b || '').trim().split('.');
    const len = Math.max(as.length, bs.length);
    for (let i = 0; i < len; i += 1) {
        const av = Number(as[i] ?? 0);
        const bv = Number(bs[i] ?? 0);
        if (!Number.isFinite(av) || !Number.isFinite(bv)) return 0;
        if (av !== bv) return av < bv ? -1 : 1;
    }
    return 0;
};

// --- Önizleme (simülatör) ------------------------------------------------

export const VERDICT = {
    DISABLED: 'DISABLED',
    MANDATORY: 'MANDATORY',
    OPTIONAL: 'OPTIONAL',
    NONE: 'NONE',
};

/**
 * "Şu sürümü kullanan bir kullanıcı ne görür?" sorusunun cevabı.
 * Backend kararının bire bir aynısı: min'in ALTI zorunlu, latest'in ALTI öneri,
 * eşitlik hiçbir şey göstermez.
 */
export const evaluateVersion = (userVersion, { enabled, minSupportedVersion, latestVersion }) => {
    if (!enabled) return VERDICT.DISABLED;
    if (!isValidVersion(userVersion)) return null;

    if (isValidVersion(minSupportedVersion) && compareVersions(userVersion, minSupportedVersion) < 0) {
        return VERDICT.MANDATORY;
    }
    if (isValidVersion(latestVersion) && compareVersions(userVersion, latestVersion) < 0) {
        return VERDICT.OPTIONAL;
    }
    return VERDICT.NONE;
};

// --- Hata kodları --------------------------------------------------------

// Metinler değişebilir, kodlar sözleşmedir.
const ERROR_MESSAGES = {
    INVALID_MIN_VERSION: 'Zorunlu minimum sürüm biçimi geçersiz. Örn. 1.2.0',
    INVALID_LATEST_VERSION: 'Güncel sürüm biçimi geçersiz. Örn. 1.5.0',
    MIN_ABOVE_LATEST: 'Zorunlu minimum sürüm, güncel sürümden büyük olamaz.',
    INVALID_STORE_URL: 'Mağaza URL\'i geçersiz. http:// veya https:// ile başlamalı.',
    INVALID_PLATFORM: 'Tanınmayan platform.',
};

export const readApiError = (err, fallback = 'Beklenmeyen bir hata oluştu.') => {
    const data = err?.response?.data;
    const code = data?.code || null;

    if (code && ERROR_MESSAGES[code]) return { code, message: ERROR_MESSAGES[code] };

    const text = data?.error || data?.message;
    if (text) return { code, message: text };

    const status = err?.response?.status;
    if (status === 403) return { code, message: 'Bu işlem için yetkiniz yok.' };
    if (status === 404) return { code, message: 'Kayıt bulunamadı.' };

    return { code, message: fallback };
};

// BaseResponse zarfı: { status, data }. Düz gövde de tolere edilir.
const unwrap = (responseData) => {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        return responseData.data;
    }
    return responseData;
};

// --- Uçlar ---------------------------------------------------------------

/**
 * GET /v1/admin/app-versions
 * Henüz kayıt yapılmamış platform boş alanlarla ve enabled:false döner —
 * bu bir hata değil, "yapılandırılmamış" demektir.
 */
export const fetchAppVersions = async () => {
    const response = await apiClient.get('/v1/admin/app-versions');
    const data = unwrap(response.data);
    return Array.isArray(data) ? data : [];
};

/** PUT /v1/admin/app-versions/{platform} — tüm alanlar birlikte yazılır. */
export const saveAppVersion = async (platform, payload) => {
    const response = await apiClient.put(`/v1/admin/app-versions/${platform}`, payload);
    return unwrap(response.data);
};
